/**
 * BL-1666 — CUSTOMER field editability (ADR 034's third rule book, beside
 * `lot-field-editability.ts` and `auction-field-editability.ts`).
 *
 * Pure IsomorphicService (ADR 010 / sharing.md §3): no I/O, no auth
 * enforcement, no writes. Client-safe and safe inside `useMemo`. Reuses the lot
 * module's {@link FieldVerdict} / {@link LockReason} vocabulary — single source,
 * no re-declaration — exactly as the auction evaluator does.
 *
 * ## The rule
 *
 * A contact channel is editable by staff until the buyer has PROVEN it on their
 * own Better Auth login, and read-only afterwards. Concretely:
 *
 * | State                                   | email    | phone    |
 * |-----------------------------------------|----------|----------|
 * | No login (walk-in, clerk-entered)       | editable | editable |
 * | Login, nothing verified                 | editable | editable |
 * | Login, `email_verified`                 | LOCKED   | editable |
 * | Login, `phone_number_verified`          | editable | LOCKED   |
 * | Login, both verified                    | LOCKED   | LOCKED   |
 *
 * ## Why email keys on `emailVerified` and not merely on "a login exists"
 *
 * BL-1666's AC 5. `requireEmailVerification` gates sign-IN, not row creation, so
 * a `customer_user` row can exist unverified — 28 of 457 on dev at filing. Those
 * accounts can never be signed into, and `customer_user.email` is immutable
 * post-sign-up (no `changeEmail` plugin is enabled), so locking on existence
 * alone would freeze a mistyped address permanently, with no path to correct it
 * from either side. Verification is the moment the value becomes the buyer's
 * assertion rather than a typist's.
 *
 * The two channels are evaluated INDEPENDENTLY: a buyer routinely verifies their
 * email at sign-up and their phone weeks later, at bid registration.
 *
 * ## What this does NOT do
 *
 * It never decides permissions — `CUSTOMERS_MANAGE` is the caller's gate, and
 * the client resolver folds it in separately (same split as
 * `makeAuctionFieldLock`). And it governs only the two mirrored contact
 * channels; every other field on the account form is outside its field set by
 * construction, which is what keeps a field-edit path structurally unable to
 * touch anything else.
 */
import type { FieldVerdict } from './lot-field-editability';

/** The customer fields this policy governs. */
export type CustomerFieldKey = 'email' | 'phone';

/**
 * The ONLY thing anything iterates (BL-1308's rule, imported wholesale).
 * A field absent from the iteration source is never handed to the gate, so its
 * lock evaporates and the write still returns 200.
 */
export const CUSTOMER_GOVERNED_FIELDS: readonly CustomerFieldKey[] = ['email', 'phone'];

/** Operator-facing lock hints. Ride `<FormField disabledHint>` / `PhoneInput helpText`. */
export const EMAIL_VERIFIED_LOCK_HINT =
  'The buyer verified this email on their website account, so it can only be changed by them.';
export const PHONE_VERIFIED_LOCK_HINT =
  'The buyer verified this number by text, so it can only be changed by them.';

/** Inputs the evaluator reads. Every one rides `CustomerAccountDto` already. */
export interface CustomerFieldEditabilityInput {
  /** `customers.customer_user_id !== null` — the buyer has a website account. */
  hasLogin: boolean;
  /** `customer_user.email_verified`. */
  emailVerified: boolean;
  /** `customer_user.phone_number_verified`. */
  phoneVerified: boolean;
}

/**
 * Per-field editability for one customer.
 *
 * @param input - Raw login + verification state (never a precomputed blob).
 * @returns A verdict for every {@link CUSTOMER_GOVERNED_FIELDS} member.
 * @sideEffect None — pure (ADR 010).
 */
export function evaluateCustomerFieldEditability(
  input: CustomerFieldEditabilityInput,
): Record<CustomerFieldKey, FieldVerdict> {
  const { hasLogin, emailVerified, phoneVerified } = input;

  // A verified flag with no login is unreachable through the DTO (the flags
  // come off `customer_user`), but the evaluator is pure and callable with any
  // shape — and "locked, with no login to explain why" is an operator-facing
  // dead end. `hasLogin` gates both channels.
  const emailLocked = hasLogin && emailVerified;
  const phoneLocked = hasLogin && phoneVerified;

  return {
    email: emailLocked
      ? { editable: false, reason: 'identity-verified', hint: EMAIL_VERIFIED_LOCK_HINT }
      : { editable: true },
    phone: phoneLocked
      ? { editable: false, reason: 'identity-verified', hint: PHONE_VERIFIED_LOCK_HINT }
      : { editable: true },
  };
}
